import classNames from "classnames";
import { Link } from "gatsby";
import * as React from "react";

import ArrrowDown from "../../assets/arrow-down.inline.svg";
import BurgerMenuImage from "../../assets/burger.inline.svg";
import LogoImage from "../../assets/logo.inline.svg";
import LogoWhiteBlueImage from "../../assets/logo-white-blue.inline.svg";
import LogoWhiteNavyImage from "../../assets/logo-white-navy.inline.svg";
import NavCloseImage from "../../assets/menu-close.inline.svg";
import { BodyText } from "../../styles";
import {
  MegaMenu,
  MenuContent,
  MenuHeader,
  MenuItem,
  MenuItemArrow,
  MenuItemDescription,
  MobileChildItem,
  MobileChildren,
  MobileMegaMenu,
  MobileNavContainer,
  MobileNavItem,
  MobileNavTrigger,
  NavContainer,
  NavItem,
  NavTrigger,
  NavWrapper,
} from "./Nav.styles";
import { navItems } from "./navItems";
import { Variant } from "./types";
import { useMenu } from "contexts/menu";

export type NavItemType = {
  title: string;
  description: string;
  description2?: string;
  url: string;
  children?: NavItemType[];
  isHeaderItem?: boolean;
};

type Props = {
  variant?: Variant;
  $isAtTop: boolean;
  activeTopLevelUrl?: string;
};

export const Nav: React.FC<Props> = ({
  $isAtTop,
  activeTopLevelUrl,
  variant = "white",
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [activeNavItem, setActiveNavItem] = React.useState<NavItemType>(
    navItems[0],
  );

  const { isMenuVisible, setIsMenuVisible } = useMenu();

  return (
    <>
      <NavWrapper $isAtTop={$isAtTop}>
        <Link to="/">
          {variant === "white" && <LogoImage />}
          {variant === "blue" && <LogoWhiteBlueImage />}
          {variant === "navy" && <LogoWhiteNavyImage />}
        </Link>
        <MobileNavContainer className="lg:hidden">
          <MobileNavTrigger
            onClick={() => setIsMobileMenuOpen((state) => !state)}
            aria-label="Menu"
            $variant={variant}
          >
            {isMobileMenuOpen ? <NavCloseImage /> : <BurgerMenuImage />}
          </MobileNavTrigger>
        </MobileNavContainer>
        <NavTrigger
          $variant={variant}
          onMouseLeave={() => setIsMenuVisible(true)}
        >
          <NavContainer className="hidden lg:flex">
            {navItems
              .filter(({ isHeaderItem }) => isHeaderItem !== false)
              .map((navItem: NavItemType) => (
                <NavItem
                  onMouseEnter={() => setActiveNavItem(navItem)}
                  key={navItem.url}
                  className={classNames(
                    activeNavItem.url === navItem.url && "isActive",
                    navItem.url === `/${activeTopLevelUrl}` && "isCurrent",
                    "lg:px-6",
                    "lg:py-8",
                  )}
                  $variant={variant}
                >
                  {navItem.title}
                </NavItem>
              ))}
          </NavContainer>

          {isMenuVisible !== false && (
            <MegaMenu className="grid-cols-12">
              <MenuHeader className="col-span-4">
                <BodyText $variant="b1impact" className="pb-4">
                  {activeNavItem.title}
                </BodyText>
                <BodyText $variant="b6reg">
                  {activeNavItem.description}
                </BodyText>
              </MenuHeader>
              {activeNavItem.children && (
                <MenuContent className="col-span-8">
                  {activeNavItem.children.map(({ description, title, url }) => (
                    <MenuItem to={url} key={url}>
                      <span className="inline-flex items-center gap-2">
                        {title}
                        <MenuItemArrow />
                      </span>
                      <MenuItemDescription className="pt-2">
                        {description}
                      </MenuItemDescription>
                    </MenuItem>
                  ))}
                </MenuContent>
              )}
            </MegaMenu>
          )}
        </NavTrigger>
      </NavWrapper>

      {isMobileMenuOpen && (
        <MobileMegaMenu className="px-8 lg:hidden">
          {navItems
            .filter(({ isHeaderItem }) => isHeaderItem !== false)
            .map(({ children, title, url }: NavItemType) => (
              <MobileNavItem htmlFor={url} key={url} className="py-6">
                <div>{title}</div>
                <ArrrowDown />
                <input type="checkbox" id={url} name={url} />
                {children && (
                  <MobileChildren className="pt-4">
                    {children.map(({ title, url }: NavItemType) => (
                      <MobileChildItem to={url} key={url} className="py-3">
                        {title}
                      </MobileChildItem>
                    ))}
                  </MobileChildren>
                )}
              </MobileNavItem>
            ))}
        </MobileMegaMenu>
      )}
    </>
  );
};
